import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getPDCPayments } from "./pdcReportService";
import { FaMoneyCheckAlt, FaPrint, FaArrowLeft, FaExclamationTriangle, FaFilter, FaUndo } from "react-icons/fa";
import "./PDCReport.css";
import { usePermissions } from "../../context/PermissionContext";
import AccessDenied from "../ui/AccessDenied";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const statusBadgeClass = (status) => {
  switch (status) {
    case "Cleared":   return "pdc-badge-cleared";
    case "Bounced":   return "pdc-badge-bounced";
    case "Cancelled": return "pdc-badge-cancelled";
    default:          return "pdc-badge-pending";
  }
};

const PDCReportPage = () => {
  const navigate = useNavigate();
  const { canView, canExport } = usePermissions();

  if (!canView("pdc")) {
    return <AccessDenied moduleName="PDC Payments" action="view" />;
  }

  // ── Filters ───────────────────────────────────────────
  const [startDate, setStartDate]       = useState("");
  const [endDate, setEndDate]           = useState("");
  const [chequeStatus, setChequeStatus] = useState("all");
  const [partyType, setPartyType]       = useState("all");

  // ── Data ──────────────────────────────────────────────
  const [cheques, setCheques]   = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const hasFetched = useRef(false);

  // ── Fetch ──────────────────────────────────────────────
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        start_date:    startDate    || undefined,
        end_date:      endDate      || undefined,
        cheque_status: chequeStatus || undefined,
        party_type:    partyType    || undefined,
      };
      const data = await getPDCPayments(filters);
      setCheques(data.cheques || []);
      setSummary(data.summary || {});
    } catch (err) {
      console.error("PDC Report fetch error:", err);
      setError("Failed to load the PDC Payment report. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, chequeStatus, partyType]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchReport();
    }
  }, [fetchReport]);

  // ── Reset ──────────────────────────────────────────────
  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setChequeStatus("all");
    setPartyType("all");
    setTimeout(() => fetchReport(), 0);
  };

  // ── Print ──────────────────────────────────────────────
  const handlePrint = () => window.print();

  return (
    <div className="pdc-page">
      {/* ── Header ─────────────────────────────────────── */}
      <div className="pdc-header">
        <h2><FaMoneyCheckAlt style={{ color: "#0284c7" }} className="me-2" /> Post-Dated Cheque Payments</h2>
        <div className="pdc-header-actions">
          <button className="pdc-btn-print" onClick={handlePrint}>
            <FaPrint className="me-1" /> Print
          </button>
          <button
            className="pdc-btn-back"
            onClick={() => navigate("/dashboard")}
          >
            <FaArrowLeft className="me-1" /> Dashboard
          </button>
        </div>
      </div>

      {/* ── Summary Cards ───────────────────────────────── */}
      {summary && (
        <div className="pdc-summary">
          <div className="pdc-card card-total">
            <div className="card-label">Total Cheques</div>
            <div className="card-value">{summary.total_count ?? 0}</div>
          </div>

          <div className="pdc-card card-amount">
            <div className="card-label">Total Amount</div>
            <div className="card-value">{fmt(summary.total_amount)}</div>
          </div>

          <div className="pdc-card card-pending">
            <div className="card-label">Pending ({summary.pending_count ?? 0})</div>
            <div className="card-value">{fmt(summary.pending_amount)}</div>
          </div>

          <div className="pdc-card card-cleared">
            <div className="card-label">Cleared ({summary.cleared_count ?? 0})</div>
            <div className="card-value">{fmt(summary.cleared_amount)}</div>
          </div>

          <div className="pdc-card card-bounced">
            <div className="card-label">Bounced ({summary.bounced_count ?? 0})</div>
            <div className="card-value">{fmt(summary.bounced_amount)}</div>
          </div>

          <div className="pdc-card card-overdue">
            <div className="card-label">Overdue ({summary.overdue_count ?? 0})</div>
            <div className="card-value">{fmt(summary.overdue_amount)}</div>
          </div>
        </div>
      )}

      {/* ── Filters ─────────────────────────────────────── */}
      <div className="pdc-filters">
        <div className="filter-group">
          <label>Cheque Date From</label>
          <input
            id="pdc-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Cheque Date To</label>
          <input
            id="pdc-end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Status</label>
          <select
            id="pdc-status-filter"
            value={chequeStatus}
            onChange={(e) => setChequeStatus(e.target.value)}
          >
            <option value="all">All</option>
            <option value="Pending">Pending</option>
            <option value="Cleared">Cleared</option>
            <option value="Bounced">Bounced</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Party Type</label>
          <select
            id="pdc-party-filter"
            value={partyType}
            onChange={(e) => setPartyType(e.target.value)}
          >
            <option value="all">All</option>
            <option value="Customer">Customer</option>
            <option value="Supplier">Supplier</option>
          </select>
        </div>

        <button id="pdc-apply-btn" className="pdc-btn-filter" onClick={fetchReport}>
          Apply Filters
        </button>
        <button id="pdc-reset-btn" className="pdc-btn-reset" onClick={handleReset}>
          Reset
        </button>
      </div>

      {/* ── Content ─────────────────────────────────────── */}
      {loading ? (
        <div className="pdc-loading">
          <div className="spinner" />
          Loading PDC payment report…
        </div>
      ) : error ? (
        <div className="pdc-loading text-danger">
          <FaExclamationTriangle className="me-2" /> {error}
        </div>
      ) : cheques.length === 0 ? (
        <div className="pdc-empty">
          No post-dated cheque payments found for the selected filters.
        </div>
      ) : (
        <div className="pdc-table-card">
          <div className="pdc-table-header">
            <strong>Cheque Payment Details</strong>
            <span>{cheques.length} record{cheques.length !== 1 ? "s" : ""}</span>
          </div>
          <div className="pdc-table-wrapper">
            <table className="pdc-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Voucher No</th>
                  <th>Payment Date</th>
                  <th>Cheque No</th>
                  <th>Cheque Date</th>
                  <th>Payee</th>
                  <th>Party Type</th>
                  <th>Bank</th>
                  <th className="num">Amount</th>
                  <th>Status</th>
                  <th>Days Until Due</th>
                </tr>
              </thead>
              <tbody>
                {cheques.map((c, idx) => (
                  <tr key={c.id}>
                    <td className="row-num">{idx + 1}</td>
                    <td style={{ fontWeight: 500 }}>{c.voucher_no}</td>
                    <td>{c.payment_date || "—"}</td>
                    <td>{c.cheque_number || "—"}</td>
                    <td>{c.cheque_date || "—"}</td>
                    <td>{c.payee}</td>
                    <td>{c.party_type || "—"}</td>
                    <td>{c.bank_name || "—"}</td>
                    <td className="num">{fmt(c.amount)}</td>
                    <td>
                      <span className={`pdc-status-badge ${statusBadgeClass(c.cheque_status)}`}>
                        {c.cheque_status}
                      </span>
                    </td>
                    <td>
                      {c.days_until_due === null ? (
                        <span className="pdc-days-na">—</span>
                      ) : c.days_until_due < 0 ? (
                        <span className="pdc-overdue">
                          {Math.abs(c.days_until_due)}d overdue
                        </span>
                      ) : c.days_until_due === 0 ? (
                        <span className="pdc-overdue">Due today</span>
                      ) : (
                        <span className="pdc-days-ok">
                          {c.days_until_due}d remaining
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="8" style={{ textAlign: "right" }}>
                    <strong>Grand Total</strong>
                  </td>
                  <td className="num">{fmt(summary?.total_amount)}</td>
                  <td colSpan="2" />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDCReportPage;
